import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import { Dispatch } from "redux";

import { BetslipSportsbookRemoveLegClick, UI__BETSLIP_SBK_REMOVE_LEG_CLICK } from "@ppb/tbd-store/actions/betslip";
import { BettingSportsbookRemoveLegAction, BETTING__SBK_REMOVE_LEG_ACTION } from "@ppb/tbd-store/actions/betting";
import {
  getBetslipGroup,
  getBetslipPlaceStatus,
  getSportsbookConfirmationCombinations,
  getSportsbookConfirmationLegs,
  getSportsbookConfirmationRunners,
} from "../../store/state/betslip-module/betslip-module-selectors";
import {
  getSportsbookBettingCombinations,
  getSportsbookBettingLegs,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../../store/state/sportsbook-betting/betting-resolver";
import {
  createSportsbookBettingRunnerSelector,
  getSportsbookBettingImplyRunnerFailures,
} from "../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { FallbackIconType } from "@ppb/the-wall-common/types";
import { Icons } from "@ppb/the-wall-icons/GenericIcon/GenericIcon";
import type { Region } from "@ppb/the-wall-icons/traps";
import { hasAnyMarketClosedFailure, hasAnyMarketSuspendedFailure } from "@ppb/tbd-store/helpers/sportsbook-betting";

import { RacingSport } from "../../constants";
import { getSelectionTypeIcon } from "../../helpers/selection-type";
import { i18n } from "../../helpers/i18n";
import { getBoostedInfo, BoostedMarketTypeMap } from "../../helpers/boosted-info";
import { buildSelection } from "../connected-sportsbook-betslip-mapper";
import { createIsConfirmStep } from "../sportsbook-betslip-confirm-mapper";
import type { BetslipLocalization, SportsbookBetslipCapabilities, SportsbookBetslipConfig } from "../../contract";

type CardProps = {
  runnerUrn: string;
  id: string;
  isPlacing: boolean;
  is90Min: boolean;
  selectionTypeIcon?: Icons;
  legId: string;
  title: string;
  subtitle: string;
  handicap?: string;
  icon?: string;
  silkFallbackIconType?: FallbackIconType;
  racingSport?: RacingSport;
  meetingCountry?: Region;
  trap?: string | number;
  isPriceBoosted?: boolean;
  boostedInfo?: BoostedMarketTypeMap;
  hasBoostSignposting: boolean;
  guaranteedPriceLabel: string;
  isGuaranteedPriceSelected: boolean;
  hintMessage?: string;
};
export type StateProps = CardProps | Record<string, never>;

export type ContainerProps = {
  id: string;
  hasAvailabilityHints?: boolean;
  shouldFocusStakeField?: boolean;
  localization: BetslipLocalization;
  config: SportsbookBetslipConfig;
  capabilities: SportsbookBetslipCapabilities;
};

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const guaranteedPriceLabel = i18n({ key: "I18N.LABELS.BEST_ODDS_GUARANTEED_REDUCED" });
  const getSportsbookBettingRunner = createSportsbookBettingRunnerSelector();
  const getIsConfirmStep = createIsConfirmStep();

  return (appState: BetslipApplicationState, { id, localization, capabilities }: ContainerProps): StateProps => {
    try {
      const isConfirmStep = getIsConfirmStep(appState);
      const group = getBetslipGroup(appState);
      const resolvers = getBettingResolvers(group);
      let combinations;
      let legs;
      let runner;

      if (isConfirmStep) {
        combinations = getSportsbookConfirmationCombinations(appState);
        legs = getSportsbookConfirmationLegs(appState);
        runner = getSportsbookConfirmationRunners(appState);
      } else {
        combinations = getSportsbookBettingCombinations(appState);
        legs = getSportsbookBettingLegs(appState);
        runner = appState.betting.sportsbookBetting.runners;
      }

      const bettingRunnersMetadata = resolvers.getMetadata(appState);
      const combination = combinations[id];
      const legId = combination.legs[0];
      const runnerId = legs[legId].runners[0];
      const metadata = bettingRunnersMetadata[runnerId];
      const bettingRunner = getSportsbookBettingRunner(runner, runnerId);

      if (!metadata) {
        return {};
      }

      const failedRunners = getSportsbookBettingImplyRunnerFailures(appState);
      const runnerFailures = failedRunners[runnerId] ?? [];
      let hintMessage;

      if (hasAnyMarketClosedFailure(runnerFailures)) {
        hintMessage = i18n({ key: "I18N.BETSLIP.SBK.MARKET_CLOSED" });
      } else if (hasAnyMarketSuspendedFailure(runnerFailures)) {
        hintMessage = i18n({ key: "I18N.BETSLIP.SBK.MARKET_SUSPENDED" });
      }

      const { name, handicap, subtitle, icon, silkFallbackType, racingSport, meetingCountry, trap } = buildSelection(
        metadata,
        localization,
        bettingRunner,
      );
      const { isGuaranteedPriceSelected } = combination;
      const placeStatus = getBetslipPlaceStatus(appState);
      const isPlacing = placeStatus === "INPROGRESS";
      const isPriceBoosted = metadata.isOddsboostMarketType;

      return {
        runnerUrn: metadata.runnerUrn,
        id: combination.id,
        isPlacing,
        is90Min: metadata.is90Min,
        selectionTypeIcon: capabilities.selectionTypeIcon.enabled
          ? getSelectionTypeIcon(metadata.marketType, metadata.isSuperSub)
          : undefined,
        legId,
        isPriceBoosted,
        boostedInfo: isPriceBoosted ? getBoostedInfo(metadata.marketType) : undefined,
        hasBoostSignposting: capabilities.priceBoostSignposting.enabled,
        title: name,
        subtitle,
        handicap,
        icon,
        racingSport,
        meetingCountry,
        trap: capabilities.trapIcon.enabled ? trap : undefined,
        silkFallbackIconType: silkFallbackType,
        guaranteedPriceLabel,
        isGuaranteedPriceSelected: isGuaranteedPriceSelected && capabilities.bestOddsGuaranteedLabel.enabled,
        hintMessage,
      };
    } catch (e) {
      console.error(e);

      return {};
    }
  };
};

export type DispatchActions = BetslipSportsbookRemoveLegClick | BettingSportsbookRemoveLegAction;

export type DispatchProps = {
  dispatchRemoveSelectionAction: ({ legId, runnerUrn }: { legId: string; runnerUrn: string }) => void;
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (
  dispatch: Dispatch<DispatchActions>,
) => ({
  dispatchRemoveSelectionAction: ({ legId, runnerUrn }) => {
    dispatch<BetslipSportsbookRemoveLegClick>({
      type: UI__BETSLIP_SBK_REMOVE_LEG_CLICK,
      payload: { legId, runnerUrn },
    });
    dispatch<BettingSportsbookRemoveLegAction>({
      type: BETTING__SBK_REMOVE_LEG_ACTION,
      payload: { legId },
    });
  },
});
